/**
 * App-wide providers. Order matters:
 *
 * 1. `MantineProvider` — theme + global CSS variables. Outermost so
 *    downstream providers and components see the theme.
 * 2. `ModalsProvider` — `useModals()` + `modals.openConfirmModal()` etc.
 *    Replaces the native `window.prompt` calls scattered through the
 *    admin views.
 * 3. `Notifications` — toast notifications via `notifications.show()`.
 * 4. `AuthConfigProvider` — fetches /api/v1/auth/config; everything
 *    Entra-aware depends on it.
 * 5. `MsalBootstrap` — initialises the MSAL PublicClientApplication.
 * 6. `IdleTimeoutProvider` — the 10-minute idle limit (ADR-0046): warns at 8,
 *    ends the session at 10, keeps the server's activity clock in step.
 * 7. `UserSettingsProvider` — loads the signed-in user's theme / font once and
 *    applies them app-wide. Inside MsalBootstrap because it needs a token, and
 *    inside MantineProvider because it drives the colour scheme.
 */

import type { PropsWithChildren } from "react";
import { MantineProvider } from "@mantine/core";
import "./brand.css";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { AuthConfigProvider } from "@/features/auth/AuthConfigProvider";
import { IdleTimeoutProvider } from "@/features/auth/IdleTimeoutProvider";
import { MsalBootstrap } from "@/features/auth/MsalBootstrap";
import { UserSettingsProvider } from "@/features/settings/UserSettingsProvider";
import { DocumentViewerProvider } from "@/features/solutions/shared/DocumentViewer";
import { theme } from "./theme";

export function AppProviders({ children }: PropsWithChildren) {
  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <ModalsProvider>
        <Notifications position="top-right" />
        <AuthConfigProvider>
          <MsalBootstrap>
            <IdleTimeoutProvider>
              <UserSettingsProvider>
                <DocumentViewerProvider>{children}</DocumentViewerProvider>
              </UserSettingsProvider>
            </IdleTimeoutProvider>
          </MsalBootstrap>
        </AuthConfigProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}
